import {TFunction} from 'i18next';
import {AnySchema, array, mixed, string, number, date} from 'yup';
import {FieldType} from '../../../shared/model/field.model';
import {getNxFileValidationSchema} from './nx-file-validation-schema';
import {
  getDefaultFieldLengthExceededError,
  getDefaultRequiredError,
  getSchemaBasedOnRequiredField,
  getTooBigError,
  getTooSmallError
} from './validation-schema-utils';
import {getDocumentFieldValidationSchema} from './document-validation-schema';

export const getCustomFieldValueSchema = (partialValidation = false, t: TFunction): AnySchema => {

  const requiredError = getDefaultRequiredError(t);
  const maxLengthSchema = string().max(512, getDefaultFieldLengthExceededError(t, 512));
  const integerMin = -2147483648;
  const integerMax = 2147483647;
  const integerSchema = number()
    .integer(getTooSmallError(t, integerMin))
    .min(integerMin, getTooSmallError(t, integerMin))
    .max(integerMax, getTooBigError(t, integerMax));

  const doubleSchema = number();
  const percentageSchema = number().min(0, getTooSmallError(t, 0)).max(100, getTooBigError(t, 100));
  const booleanSchema = string().oneOf(['true', 'false']);
  const singleDictionarySchema = string();
    const dateSchema = date()
        .typeError('Invalid date')
        .test('is-valid-date', 'Invalid date', value => {
            if (!value) return true; // allow empty when not required
            return value instanceof Date && !isNaN(value.getTime());
        });

  const getFieldSchema = (required = false): AnySchema =>
    mixed().when('fieldType', fieldType => {
      switch (fieldType) {
        case FieldType.FILE:
          return getNxFileValidationSchema(required, requiredError);
        case FieldType.DOCUMENT:
          return getDocumentFieldValidationSchema(required, requiredError, t);
        case FieldType.DICTIONARY_MULTI:
          return required ? array().min(1, requiredError) : array();
        case FieldType.DICTIONARY_SINGLE:
          return required ? singleDictionarySchema.required(requiredError) : singleDictionarySchema;
        case FieldType.STRING:
          return required ? maxLengthSchema.required(requiredError) : maxLengthSchema;
        case FieldType.INTEGER:
          return required ? integerSchema.required(requiredError) : integerSchema;
        case FieldType.DOUBLE:
        case FieldType.CASH:
          return required ? doubleSchema.required(requiredError) : doubleSchema;
        case FieldType.PERCENTAGE:
          return required ? percentageSchema.required(requiredError) : percentageSchema;
        case FieldType.BOOLEAN:
          return required ? booleanSchema.required(requiredError) : booleanSchema;
        case FieldType.DATE:
          return required ? dateSchema.required(requiredError) : dateSchema;
        default:
          return required ? mixed().required(requiredError) : mixed();
      }
    });

  return getSchemaBasedOnRequiredField(partialValidation, getFieldSchema(), getFieldSchema(true));
};
